import { useState, useEffect, useCallback } from 'react';

import { DetectionResult, UniqueBirdDetection, DetectionEvent, LocationData } from '../types';

export const useUniqueBirdDetections = (detectionResults: DetectionResult[], currentLocation?: LocationData) => {
  const [uniqueDetections, setUniqueDetections] = useState<Map<string, UniqueBirdDetection>>(new Map());


  const calculateConfidenceStats = useCallback((events: DetectionEvent[]) => {
    if (events.length === 0) {return { max: 0, min: 0, avg: 0 };}
    
    const confidences = events.map(e => e.confidence);
    const max = Math.max(...confidences);
    const min = Math.min(...confidences);
    const avg = confidences.reduce((sum, conf) => sum + conf, 0) / confidences.length;
    
    return { max, min, avg };
  }, []);


  useEffect(() => {
    // Rebuild unique detections from scratch each time
    const newUniqueDetections = new Map<string, UniqueBirdDetection>();
    
    detectionResults.forEach((result, resultIndex) => {
      if (result.detections && result.detections.length > 0) {
        result.detections.forEach((detection, detectionIndex) => {
          // Create a better unique key using both species and common_name
          const uniqueKey = `${detection.species || 'unknown'}_${detection.common_name || 'unknown'}`.toLowerCase().replace(/\s+/g, '_');
          
          const detectionEvent: DetectionEvent = {
            id: `${result.timestamp}_${resultIndex}_${detectionIndex}`, // Use deterministic ID
            confidence: detection.confidence,
            start_time: detection.start_time,
            end_time: detection.end_time,
            timestamp: result.timestamp,
            audio_length: result.audio_length,
            location: currentLocation
          };

          const existing = newUniqueDetections.get(uniqueKey);

          if (existing) {
            const updatedEvents = [...existing.detection_events, detectionEvent];
            const stats = calculateConfidenceStats(updatedEvents);
            
            const updated: UniqueBirdDetection = {
              ...existing,
              last_detected: result.timestamp,
              detection_count: updatedEvents.length,
              session_detections: existing.session_detections + 1,
              max_confidence: stats.max,
              min_confidence: stats.min,
              avg_confidence: stats.avg,
              detection_events: updatedEvents
            };
            
            newUniqueDetections.set(uniqueKey, updated);
          } else {
            const newDetection: UniqueBirdDetection = {
              species: detection.species || 'Unknown species',
              common_name: detection.common_name || 'Unknown bird',
              first_detected: result.timestamp,
              last_detected: result.timestamp,
              detection_count: 1,
              session_detections: 1,
              max_confidence: detection.confidence || 0,
              min_confidence: detection.confidence || 0,
              avg_confidence: detection.confidence || 0,
              detection_events: [detectionEvent],
              unique_key: uniqueKey
            };
            
            newUniqueDetections.set(uniqueKey, newDetection);
          }
        });
      }
    });
    
    setUniqueDetections(newUniqueDetections);
  }, [detectionResults, calculateConfidenceStats, currentLocation]);

  const getUniqueDetectionsList = useCallback((): UniqueBirdDetection[] => {
    return Array.from(uniqueDetections.values()).sort((a, b) => 
      new Date(b.last_detected).getTime() - new Date(a.last_detected).getTime()
    );
  }, [uniqueDetections]);

  const getDetectionBySpecies = useCallback((species: string): UniqueBirdDetection | undefined => {
    return uniqueDetections.get(species);
  }, [uniqueDetections]);

  const getTotalUniqueSpecies = useCallback((): number => {
    return uniqueDetections.size;
  }, [uniqueDetections]);

  const getTotalDetectionEvents = useCallback((): number => {
    return Array.from(uniqueDetections.values()).reduce((total, bird) => total + bird.detection_count, 0);
  }, [uniqueDetections]);

  const getRecentDetections = useCallback((limit: number = 10): UniqueBirdDetection[] => {
    return getUniqueDetectionsList().slice(0, limit);
  }, [getUniqueDetectionsList]);

  const clearDetections = useCallback(() => {
    setUniqueDetections(new Map());
  }, []);

  return {
    uniqueDetections: getUniqueDetectionsList(),
    getDetectionBySpecies,
    getTotalUniqueSpecies,
    getTotalDetectionEvents,
    getRecentDetections,
    clearDetections
  };
};